/**
 * Consultation PDF Preview Modal.
 *
 * Renders the same layout as the PDF worker consultation.html template.
 * Single continuous page — content flows naturally, no page breaks.
 *
 * PII NOTE: patient name shown as "[Patient Name]" placeholder per Story 12.
 */

import { Modal } from "@hiya/ui";

interface PreviewProps {
  open: boolean;
  onClose: () => void;
  consultationDate: string;
  doctorNote: string;
  planNextSteps: string;
  recommendations: string;
  doctorName: string;
  signatureUrl: string | null;
}

const HEADER_BG = "#F2D5BB";
const HEADER_FG = "#A66046";
const PAGE_WIDTH = 595;
const SCALE = 0.75;

function PageHeader({ title, subtitle }: Readonly<{ title: string; subtitle: string }>) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: HEADER_BG,
        padding: 24,
      }}
    >
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: HEADER_FG, lineHeight: 1.2 }}>
          {title}
        </div>
        <div style={{ fontSize: 16, fontWeight: 500, color: HEADER_FG, lineHeight: 1.5 }}>
          {subtitle}
        </div>
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color: HEADER_FG, letterSpacing: 3 }}>
        HIYA
      </div>
    </div>
  );
}

function SectionBlock({
  icon,
  title,
  content,
}: Readonly<{
  icon: React.ReactNode;
  title: string;
  content: string;
}>) {
  return (
    <div style={{ padding: "16px 24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        {icon}
        <div style={{ fontSize: 16, fontWeight: 600, color: "#1C1C1C", lineHeight: 1.2 }}>
          {title}
        </div>
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 400,
          lineHeight: 1.5,
          color: "#1C1C1C",
          whiteSpace: "pre-wrap",
        }}
      >
        {content || "(empty)"}
      </div>
    </div>
  );
}

const EditIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1c1c1c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13.26 3.6l-8.21 8.69c-.31.33-.61.98-.67 1.43l-.37 3.24c-.13 1.17.71 1.97 1.87 1.77l3.22-.55c.45-.08 1.08-.4 1.39-.75l8.21-8.69c1.42-1.5 2.06-3.21-.15-5.3-2.2-2.07-3.87-1.34-5.29.16z" />
    <path d="M11.89 5.05a6.126 6.126 0 005.45 5.15" />
    <path d="M3 22h18" />
  </svg>
);

const HealthIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1c1c1c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2v4M18 2v4" />
    <path d="M8.5 14.25l2.5 2.5 4.5-5" />
    <path d="M3.5 9.09h17" />
    <path d="M21 8.5V17c0 3-1.5 5-5 5H8c-3.5 0-5-2-5-5V8.5c0-3 1.5-5 5-5h8c3.5 0 5 2 5 5z" />
  </svg>
);

const DocIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1c1c1c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" />
    <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8z" />
    <path d="M13 13l-4 4M9 13l4 4" />
  </svg>
);

export function ConsultationPreviewModal({
  open,
  onClose,
  consultationDate,
  doctorNote,
  planNextSteps,
  recommendations,
  doctorName,
  signatureUrl,
}: Readonly<PreviewProps>) {
  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title="Preview">
      <div
        style={{
          maxHeight: "80vh",
          overflowY: "auto",
          padding: "24px 0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          background: "#f0f0f0",
        }}
      >
        {/* Single continuous page — matches PDF template */}
        <div
          style={{
            width: PAGE_WIDTH,
            background: "#fff",
            fontFamily: '"Lato", sans-serif',
            fontSize: 14,
            color: "#1C1C1C",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 2px 16px rgba(0,0,0,0.12)",
            borderRadius: 4,
            overflow: "visible",
            transform: `scale(${SCALE})`,
            transformOrigin: "top center",
          }}
        >
          <div style={{ flex: 1 }}>
            <PageHeader title="Consultation Results" subtitle="A note from your doctor" />

            {/* Info card — Patient Name + Date only (no email/phone per HH-214) */}
            <div
              style={{
                margin: "16px 24px",
                background: "#FBFBFB",
                border: "1px solid #F4F4F4",
                borderRadius: 16,
                padding: 20,
              }}
            >
              <div style={{ display: "flex", gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "#A4A4A4", marginBottom: 8 }}>
                    Patient Name
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "#1C1C1C" }}>
                    [Patient Name]
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "#A4A4A4", marginBottom: 8 }}>
                    Date of consultation
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "#1C1C1C" }}>
                    {consultationDate}
                  </div>
                </div>
              </div>
            </div>

            <SectionBlock icon={<EditIcon />} title="Doctor Note" content={doctorNote} />
            <SectionBlock icon={<HealthIcon />} title="Plan / Next Steps" content={planNextSteps} />
            <SectionBlock icon={<DocIcon />} title="Recommendations" content={recommendations} />

            {/* Signature block */}
            {doctorName && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px" }}>
                <div>
                  <div style={{ fontSize: 14, color: "#1C1C1C", lineHeight: 1.5 }}>Kindest regards,</div>
                  <div style={{ fontSize: 14, color: "#A4A4A4", lineHeight: 1.5 }}>{doctorName}</div>
                </div>
                {signatureUrl && (
                  <img
                    src={signatureUrl}
                    alt="Doctor signature"
                    style={{ height: 80, width: 120, objectFit: "contain" }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: HEADER_BG,
              padding: "12px 24px",
              fontSize: 14,
              fontWeight: 500,
              color: HEADER_FG,
              lineHeight: 1.5,
              marginTop: "auto",
            }}
          >
            <span>Hiya Healthcare</span>
            <span />
          </div>
        </div>
      </div>
    </Modal>
  );
}
